import axios from 'axios'

const API_URL = '/admin/blocks'

class BlockService {
    /**
     * Normalize a block coming from the API into a payload accepted by
     * store/update. The API returns `products` as full product models, but
     * the write endpoints expect a list of { products_id, sort_order },
     * so every other key (id, created_at, pivot, products_count, ...) is dropped.
     * @param {Object} block - Block as returned by list()/get()
     * @param {Object} overrides - Fields to override
     * @returns {Object}
     */
    toPayload(block = {}, overrides = {}) {
        const merged = { ...block, ...overrides }

        return {
            name: merged.name,
            name_ua: merged.name_ua,
            orientation: merged.orientation,
            icon: merged.icon,
            is_active: merged.is_active,
            sort_order: merged.sort_order,
            products: (merged.products || []).map((product, index) => ({
                products_id: product.products_id ?? product.id,
                sort_order: product.sort_order ?? product.pivot?.sort_order ?? index
            }))
        }
    }

    /**
     * Get list of all blocks
     * @param {Object} params - Query parameters (search, is_active, orientation, per_page)
     * @returns {Promise}
     */
    list(params = {}) {
        return axios.get(API_URL, { params })
    }

    /**
     * Get single block by ID
     * @param {Number|String} id - Block ID
     * @returns {Promise}
     */
    get(id) {
        return axios.get(`${API_URL}/${id}`)
    }

    /**
     * Create new block
     * @param {Object} data - Block data
     * @returns {Promise}
     */
    create(data) {
        return axios.post(API_URL, data)
    }

    /**
     * Update existing block
     * @param {Number|String} id - Block ID
     * @param {Object} data - Block data
     * @returns {Promise}
     */
    update(id, data) {
        return axios.put(`${API_URL}/${id}`, data)
    }

    /**
     * Delete block
     * @param {Number|String} id - Block ID
     * @returns {Promise}
     */
    remove(id) {
        return axios.delete(`${API_URL}/${id}`)
    }

    /**
     * Update sort order for multiple blocks
     * @param {Object} data - Object with items array
     * @returns {Promise}
     */
    updateSortOrder(data) {
        return axios.post(`${API_URL}/sort-order`, data)
    }

    /**
     * Toggle block active status
     * @param {Number|String} id - Block ID
     * @param {Boolean} isActive - New active status
     * @returns {Promise}
     */
    async toggleActive(id, isActive) {
        const response = await this.get(id)
        return this.update(id, this.toPayload(response.data, { is_active: isActive }))
    }

    /**
     * Get blocks by orientation
     * @param {String} orientation - 'horizontal' or 'vertical'
     * @returns {Promise}
     */
    getByOrientation(orientation) {
        return this.list({ orientation })
    }

    /**
     * Duplicate block
     * @param {Number|String} id - Block ID to duplicate
     * @returns {Promise}
     */
    async duplicate(id) {
        const response = await this.get(id)
        const block = response.data

        const newBlock = this.toPayload(block, {
            name: `${block.name} (копия)`,
            name_ua: `${block.name_ua} (копія)`,
            is_active: false,
            sort_order: block.sort_order + 1
        })

        return this.create(newBlock)
    }
}

export default new BlockService()